import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { EventCard } from "@/components/EventCard";
import { BookingCalendar } from "@/components/BookingCalendar";
import { getUpcomingEvents, getBookedDates } from "@/lib/api/public-data.functions";
import { SITE } from "@/config/site";

const data = queryOptions({
  queryKey: ["events-page"],
  queryFn: async () => {
    const [events, booked] = await Promise.all([getUpcomingEvents(), getBookedDates()]);
    return { events, booked };
  },
});

export const Route = createFileRoute("/events")({
  head: ({ loaderData }: { loaderData?: { events: any[]; booked: any[] } }) => ({
    meta: [
      { name: "robots", content: "index, follow" },
      { title: "Upcoming Events · Surer Adda With Sanchayeeta" },
      { name: "description", content: "Upcoming live shows, concerts and events by Bengali singer Sanchayeeta Seal. View posters, dates & venues, and check booking availability." },
      { property: "og:title", content: "Upcoming Events · Surer Adda" },
      { property: "og:description", content: "Live shows, concerts and event dates featuring Sanchayeeta Seal." },
      { property: "og:url", content: `${SITE.url}/events` },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/events` }],
    scripts: loaderData?.events?.length
      ? [{
          type: "application/ld+json",
          children: JSON.stringify(loaderData.events.map((e: any) => ({
            "@context": "https://schema.org",
            "@type": "Event",
            name: e.title ?? e.name,
            startDate: e.event_date ?? e.start_date ?? e.date,
            eventStatus: "https://schema.org/EventScheduled",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            location: { "@type": "Place", name: e.venue ?? e.location ?? "India", address: e.location ?? e.venue ?? "India" },
            image: e.poster_url ?? e.image_url,
            performer: { "@type": "Person", name: "Sanchayeeta Seal" },
            organizer: { "@type": "Organization", name: "Surer Adda With Sanchayeeta" },
          }))),
        }]
      : [],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(data),
  component: EventsPage,
  errorComponent: ({ error }) => { console.error(error); return <div className="p-10 text-center">Something went wrong loading events.</div>; },
});

function EventsPage() {
  const { data: d } = useSuspenseQuery(data);
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Live</p>
        <h1 className="mt-2 text-4xl sm:text-5xl">Upcoming Events</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">Catch Sanchayeeta on stage. New dates added regularly.</p>
        <div className="mt-10">
          {d.events.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground">
              No upcoming events listed yet. Check back soon.
            </p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {d.events.map((e) => <EventCard key={e.id} event={e} />)}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <h2 className="text-3xl">Booking Calendar — Next 30 Days</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          <span className="mr-2 inline-block h-3 w-3 rounded-full bg-green-500 align-middle" />
          Green = confirmed booking on that date.
        </p>
        <div className="mt-8">
          <BookingCalendar bookedDates={d.booked} />
        </div>
      </section>
      </main>
      <SiteFooter />
    </div>
  );
}
